import { useQuery } from '@tanstack/react-query';
import { posService } from '@/services/pos.service';
import { SHIPPING_CARRIERS, type ShippingCarrierMeta } from '@/constants/shippingCarriers';
import { DEFAULT_SHIPPING_COD_LIMIT, type ShippingCodLimit } from '@tcr-erp/shared';

export type CarrierStatus = 'connected' | 'connectable' | 'coming_soon';

export interface CarrierOption extends ShippingCarrierMeta {
  /** Chọn được hay không — chỉ hãng đã bật trong Settings mới true. */
  enabled: boolean;
  status: CarrierStatus;
  /** Hạn mức COD do Settings đặt. `max = 0` = không chặn trên. */
  codLimit: ShippingCodLimit;
}

/**
 * Catalog hãng (hằng số FE) ghép với trạng thái bật/tắt (BE).
 *
 * Catalog nằm ở FE vì Settings chỉ cấu hình được 3 hãng, nhưng thu ngân cần
 * thấy đủ danh sách để biết hệ thống có hỗ trợ hãng nào. Hãng không
 * `settingsSupported` luôn bị ép `enabled=false` — kể cả khi payload BE nói
 * `enabled: true` (payload cũ/giả mạo) — vì Settings không có gì để cấu hình
 * cho hãng đó nên không được phép chọn được.
 */
export function useShippingCarriers(): {
  carriers: CarrierOption[];
  isLoading: boolean;
  refetch: () => void;
} {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['shipping', 'carriers'],
    queryFn: () => posService.listShippingCarriers(),
    staleTime: 5 * 60 * 1000,
  });

  const enabledByProvider = new Map((data ?? []).map((c) => [c.provider, c.enabled]));
  const codLimitByProvider = new Map(
    (data ?? []).map((c) => [c.provider, c.cod_limit ?? DEFAULT_SHIPPING_COD_LIMIT]),
  );

  const carriers: CarrierOption[] = SHIPPING_CARRIERS.map((meta) => {
    const enabled = meta.settingsSupported && (enabledByProvider.get(meta.code) ?? false);
    const status: CarrierStatus = !meta.settingsSupported
      ? 'coming_soon'
      : enabled
        ? 'connected'
        : 'connectable';
    return {
      ...meta,
      enabled,
      status,
      codLimit: codLimitByProvider.get(meta.code) ?? DEFAULT_SHIPPING_COD_LIMIT,
    };
  });

  return { carriers, isLoading, refetch: () => void refetch() };
}
